// AIReviewPanel.js - Side panel for AI Review with chat highlighting
import React, { useState, useEffect, useRef } from 'react';
import { X, Brain, AlertTriangle, CheckCircle, Edit3, Save, RotateCcw, TrendingUp, Shield, Heart, Target, Clock, Globe, User, MessageSquare, UserCheck, ChevronLeft, ChevronRight } from 'lucide-react';
import apiService from '../services/api';

const AIReviewPanel = ({ 
  isOpen, 
  onClose, 
  messageId,                    // v2.0: Use messageId instead of conversationId
  token, 
  patientName = null,
  highlightedMessageId = null,  // ID of the message being reviewed
  onHighlightMessage = null,    // Callback to highlight message in chat
  chatMessages = []             // Chat messages for context
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [aiReview, setAIReview] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isCreatingReview, setIsCreatingReview] = useState(false);
  
  const panelRef = useRef(null);

  // Load AI Review when panel opens - v2.0 uses messageId
  useEffect(() => {
    if (isOpen && (messageId || highlightedMessageId) && token) {
      loadAIReview();
    }
  }, [isOpen, messageId, highlightedMessageId, token]);

  const loadAIReview = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // v2.0: Use messageId (prefer explicit messageId, fallback to highlightedMessageId)
      const rawMessageId = messageId || highlightedMessageId;
      if (!rawMessageId) {
        throw new Error('No message ID provided for AI Review lookup');
      }
      
      // Clean message ID by removing any suffixes like _assistant
      const targetMessageId = rawMessageId.replace(/_assistant$|_user$/, '');
      
      console.log(`📋 Loading AI Review v2.0 for message ${rawMessageId} (cleaned: ${targetMessageId})`);
      const data = await apiService.getAIReview(token, targetMessageId);
      
      setAIReview(data);
      console.log('✅ AI Review v2.0 loaded:', data);
      
      // Highlight the reviewed message if available
      if (data.message_id && onHighlightMessage) {
        onHighlightMessage(data.message_id);
      }
    } catch (err) {
      console.error('❌ Error loading AI Review v2.0:', err);
      
      // Handle different error types gracefully
      if (err.response?.status === 404 || err.message.includes('AI Review not found') || err.message.includes('404')) {
        // No AI Review exists yet - this is normal, show option to create one
        setError(null);
        setAIReview(null);
        console.log('📋 No AI Review found for this message - ready to create new one');
      } else if (err.response?.status === 500) {
        // Server error - show user-friendly message
        setError('Serverfel vid hämtning av AI-granskning. Försök igen om en stund.');
        console.error('Server error details:', err.response?.data);
      } else if (err.response?.status === 403) {
        // Access denied - user not authorized
        setError('Du har inte behörighet att granska AI-analys. Kontakta administratör.');
      } else if (err.response?.status === 401) {
        // Unauthorized - token expired (handled by axios interceptor but good to be explicit)
        setError('Din session har gått ut. Logga in igen.');
      } else if (err.code === 'NETWORK_ERROR' || !err.response) {
        // Network issues
        setError('Nätverksfel. Kontrollera din internetanslutning och försök igen.');
      } else {
        // Generic error
        setError(`Fel vid hämtning av AI-granskning: ${err.response?.data?.detail || err.message || 'Okänt fel'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!hasChanges) return;
    
    setSaving(true);
    setError(null);
    
    try {
      // v2.0: Build update request with JSONB structure
      const targetMessageId = messageId || highlightedMessageId || aiReview?.message_id;
      if (!targetMessageId) {
        throw new Error('No message ID available for update');
      }
      
      const updates = {
        // v2.0: Use detailed_scores JSONB structure
        detailed_scores: {
          overall_quality: aiReview.detailed_scores?.overall_quality || aiReview.overall_quality,
          empathy_level: aiReview.detailed_scores?.empathy_level || aiReview.empathy_level,
          safety_assessment: aiReview.detailed_scores?.safety_assessment || aiReview.safety_assessment,
          technique_execution: aiReview.detailed_scores?.technique_execution || aiReview.technique_execution,
          therapeutic_appropriateness: aiReview.detailed_scores?.therapeutic_appropriateness || aiReview.therapeutic_appropriateness,
          timing_appropriateness: aiReview.detailed_scores?.timing_appropriateness || aiReview.timing_appropriateness,
          cultural_appropriateness: aiReview.detailed_scores?.cultural_appropriateness || aiReview.cultural_appropriateness,
          personalization: aiReview.detailed_scores?.personalization || aiReview.personalization,
          clarity_communication: aiReview.detailed_scores?.clarity_communication || aiReview.clarity_communication,
          professional_boundaries: aiReview.detailed_scores?.professional_boundaries || aiReview.professional_boundaries
        },
        needs_review: aiReview.needs_review,
        quality_concerns: aiReview.quality_concerns,
        safety_concerns: aiReview.safety_concerns
      };
      
      console.log('💾 Saving AI Review v2.0 updates:', updates);
      await apiService.updateAIReview(token, targetMessageId, updates);
      
      setIsEditing(false);
      setHasChanges(false);
      console.log('✅ AI Review v2.0 updated successfully');
      
      await loadAIReview();
    } catch (err) {
      console.error('❌ Error saving AI Review v2.0:', err);
      setError(err.message || 'Kunde inte spara ändringar');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    loadAIReview();
    setIsEditing(false);
    setHasChanges(false);
  };

  const updateScore = (field, value) => {
    setAIReview(prev => ({
      ...prev,
      // v2.0: Update both top-level and detailed_scores for backward compatibility
      [field]: parseFloat(value),
      detailed_scores: {
        ...prev.detailed_scores,
        [field]: parseFloat(value)
      }
    }));
    setHasChanges(true);
  };

  const updateFlag = (field, value) => {
    setAIReview(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  const createAIReview = async () => {
    if (!highlightedMessageId) return;
    
    setIsCreatingReview(true);
    setError(null);
    
    try {
      // Find the highlighted message and the user message before it
      const messageIndex = chatMessages.findIndex(msg => msg.id === highlightedMessageId);
      const aiMessage = chatMessages[messageIndex];
      const userMessage = messageIndex > 0 ? chatMessages[messageIndex - 1] : null;
      
      if (!aiMessage || aiMessage.role !== 'assistant') {
        throw new Error('Kan endast skapa AI Review för AI-svar');
      }
      
      if (!userMessage || userMessage.role !== 'user') {
        throw new Error('Kan inte hitta klientens meddelande');
      }
      
      // v2.0: Extract patient_id from context or use a placeholder
      const patientId = aiMessage.patient_id || userMessage.patient_id || 'unknown';
      
      console.log('🔄 Creating AI Review v2.0 for message:', {
        messageId: highlightedMessageId,
        patientId: patientId,
        aiMessage: aiMessage.content.substring(0, 100) + '...',
        userMessage: userMessage.content.substring(0, 100) + '...'
      });
      
      // v2.0: Create AI Review with new structure
      await apiService.createAIReview(token, {
        message_id: highlightedMessageId,
        patient_id: patientId,
        user_message: userMessage.content,
        ai_response: aiMessage.content,
        session_context: {
          timestamp: aiMessage.timestamp,
          conversation_length: chatMessages.length,
          message_index: messageIndex
        }
      });
      
      console.log('✅ AI Review v2.0 created successfully');
      
      // Reload the review
      await loadAIReview();
    } catch (err) {
      console.error('❌ Error creating AI Review v2.0:', err);
      setError(err.message || 'Kunde inte skapa AI Review');
    } finally {
      setIsCreatingReview(false);
    }
  };

  const getQualityColor = (score) => {
    if (score >= 8) return 'text-green-600 bg-green-50 border-green-200';
    if (score >= 6) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    return 'text-red-600 bg-red-50 border-red-200';
  };

  const getQualityIcon = (score) => {
    if (score >= 8) return <CheckCircle className="w-3 h-3" />;
    if (score >= 6) return <AlertTriangle className="w-3 h-3" />;
    return <AlertTriangle className="w-3 h-3" />;
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString('sv-SE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Compact score display for panel - v2.0 compatible
  const ScoreDisplay = ({ label, value, field, icon: Icon, disabled = false }) => {
    // v2.0: Get value from detailed_scores or fallback to top-level for backward compatibility
    const scoreValue = aiReview?.detailed_scores?.[field] ?? value ?? 0;
    
    return (
      <div className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
        <div className="flex items-center gap-2 flex-1">
          {Icon && <Icon className="w-3 h-3 text-gray-500" />}
          <span className="text-xs text-gray-600 truncate">{label}</span>
        </div>
        {isEditing && !disabled ? (
          <input
            type="number"
            min="0"
            max="10"
            step="0.1"
            value={scoreValue}
            onChange={(e) => updateScore(field, e.target.value)}
            className="w-12 px-1 py-0.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        ) : (
          <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs border ${getQualityColor(scoreValue)}`}>
            {getQualityIcon(scoreValue)}
            <span className="font-medium">{scoreValue.toFixed(1)}</span>
          </div>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      {!isCollapsed && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-25 z-40"
          onClick={onClose}
        />
      )}
      
      {/* Panel */}
      <div 
        ref={panelRef}
        className={`fixed top-0 right-0 h-full bg-white shadow-xl z-50 border-l border-gray-200 transition-all duration-300 ${
          isCollapsed ? 'w-12' : 'w-96'
        }`}
      >
        {/* Collapse/Expand Button */}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-full bg-white border border-gray-200 rounded-l-md p-1 shadow-sm hover:bg-gray-50"
        >
          {isCollapsed ? (
            <ChevronLeft className="w-4 h-4 text-gray-600" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-600" />
          )}
        </button>

        {!isCollapsed && (
          <>
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-purple-500 to-blue-600">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                  <Brain className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-white">AI-granskning</h3>
                  {patientName && (
                    <p className="text-xs text-white text-opacity-80">{patientName}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1">
                {isEditing && hasChanges && (
                  <>
                    <button
                      onClick={handleReset}
                      className="p-1 text-white text-opacity-70 hover:text-opacity-100"
                      disabled={saving}
                    >
                      <RotateCcw className="w-4 h-4" />
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="p-1 text-white text-opacity-70 hover:text-opacity-100"
                    >
                      {saving ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Save className="w-4 h-4" />
                      )}
                    </button>
                  </>
                )}
                <button
                  onClick={onClose}
                  className="p-1 text-white text-opacity-70 hover:text-opacity-100"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="h-full overflow-y-auto pb-20">
              {loading ? (
                <div className="flex flex-col items-center justify-center py-8">
                  <div className="w-6 h-6 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
                  <span className="mt-2 text-xs text-gray-600">Laddar...</span>
                </div>
              ) : error ? (
                <div className="p-4">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <div className="flex items-center">
                      <AlertTriangle className="w-4 h-4 text-red-600 mr-2" />
                      <span className="text-xs text-red-800">{error}</span>
                    </div>
                  </div>
                </div>
              ) : aiReview ? (
                <div className="p-4 space-y-4">
                  {/* Message Context - v2.0 encrypted message display */}
                  {highlightedMessageId && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <h4 className="text-xs font-medium text-blue-800 mb-2">Analyserat meddelande</h4>
                      <div className="text-xs text-blue-700">
                        ID: {highlightedMessageId}
                        {aiReview?.message_id && aiReview.message_id !== highlightedMessageId && (
                          <div className="mt-1 text-blue-600">
                            Granskning: {aiReview.message_id}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Encrypted Messages - Only available to therapists in v2.0 */}
                  {(aiReview.user_message || aiReview.ai_response) && (
                    <div className="bg-white border border-gray-200 rounded-lg p-3">
                      <h4 className="text-sm font-medium text-gray-900 mb-3">Meddelandeinnehåll (dekrypterat)</h4>
                      
                      {aiReview.user_message && (
                        <div className="mb-3">
                          <h5 className="text-xs font-medium text-gray-700 mb-1">Klientens meddelande:</h5>
                          <div className="bg-gray-50 rounded p-2 text-xs text-gray-800 leading-relaxed">
                            {aiReview.user_message}
                          </div>
                        </div>
                      )}
                      
                      {aiReview.ai_response && (
                        <div>
                          <h5 className="text-xs font-medium text-gray-700 mb-1">AI:s svar:</h5>
                          <div className="bg-blue-50 rounded p-2 text-xs text-gray-800 leading-relaxed">
                            {aiReview.ai_response}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Quality Scores */}
                  <div className="bg-white border border-gray-200 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-medium text-gray-900">Kvalitetspoäng</h4>
                      {!isEditing && (
                        <button
                          onClick={() => setIsEditing(true)}
                          className="p-1 text-gray-500 hover:text-gray-700"
                        >
                          <Edit3 className="w-3 h-3" />
                        </button>
                      )}
                    </div>

                    <div className="space-y-0">
                      <ScoreDisplay 
                        label="Övergripande"
                        field="overall_quality"
                        icon={TrendingUp}
                      />
                      
                      <ScoreDisplay 
                        label="Empati"
                        field="empathy_level"
                        icon={Heart}
                      />
                      
                      <ScoreDisplay 
                        label="Säkerhet"
                        field="safety_assessment"
                        icon={Shield}
                      />
                      
                      <ScoreDisplay 
                        label="Teknik"
                        field="technique_execution"
                        icon={Target}
                      />
                      
                      <ScoreDisplay 
                        label="Lämplighet"
                        field="therapeutic_appropriateness"
                        icon={UserCheck}
                      />
                      
                      <ScoreDisplay 
                        label="Timing"
                        field="timing_appropriateness"
                        icon={Clock}
                        disabled={true}
                      />
                      
                      <ScoreDisplay 
                        label="Kulturell"
                        field="cultural_appropriateness"
                        icon={Globe}
                        disabled={true}
                      />
                      
                      <ScoreDisplay 
                        label="Personlig"
                        field="personalization"
                        icon={User}
                        disabled={true}
                      />
                      
                      <ScoreDisplay 
                        label="Klarhet"
                        field="clarity_communication"
                        icon={MessageSquare}
                        disabled={true}
                      />
                      
                      <ScoreDisplay 
                        label="Gränser"
                        field="professional_boundaries"
                        icon={Shield}
                        disabled={true}
                      />
                    </div>

                    {/* Composite Score */}
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-gray-700">Totalt:</span>
                        <div className={`px-2 py-1 rounded text-xs font-bold ${
                          aiReview.composite_score >= 8 ? 'bg-green-100 text-green-800' :
                          aiReview.composite_score >= 6 ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {(aiReview.composite_score || 0).toFixed(1)}/10
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Flags */}
                  <div className="bg-white border border-gray-200 rounded-lg p-3">
                    <h4 className="text-sm font-medium text-gray-900 mb-3">Flaggor</h4>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-600">Behöver granskning</span>
                        <div className={`px-2 py-0.5 rounded text-xs font-medium ${
                          aiReview.needs_review ? 'bg-orange-100 text-orange-800' : 'bg-green-100 text-green-800'
                        }`}>
                          {aiReview.needs_review ? 'Ja' : 'Nej'}
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-600">Kvalitetsproblem</span>
                        <div className={`px-2 py-0.5 rounded text-xs font-medium ${
                          aiReview.quality_concerns ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                        }`}>
                          {aiReview.quality_concerns ? 'Ja' : 'Nej'}
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-600">Säkerhetsproblem</span>
                        <div className={`px-2 py-0.5 rounded text-xs font-medium ${
                          aiReview.safety_concerns ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                        }`}>
                          {aiReview.safety_concerns ? 'Ja' : 'Nej'}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* KBT Analysis - v2.0 JSONB structure */}
                  {aiReview.kbt_analysis && Object.keys(aiReview.kbt_analysis).length > 0 && (
                    <div className="bg-white border border-gray-200 rounded-lg p-3">
                      <h4 className="text-sm font-medium text-gray-900 mb-3">KBT-analys</h4>
                      <div className="space-y-2">
                        {/* Show techniques_used if available */}
                        {aiReview.kbt_analysis.techniques_used && (
                          <div className="border-l-3 border-blue-200 pl-2">
                            <h5 className="text-xs font-medium text-gray-900">Använda tekniker</h5>
                            <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                              {Array.isArray(aiReview.kbt_analysis.techniques_used) 
                                ? aiReview.kbt_analysis.techniques_used.join(', ')
                                : aiReview.kbt_analysis.techniques_used}
                            </p>
                          </div>
                        )}
                        
                        {/* Show effectiveness_assessment if available */}
                        {aiReview.kbt_analysis.effectiveness_assessment && (
                          <div className="border-l-3 border-green-200 pl-2">
                            <h5 className="text-xs font-medium text-gray-900">Effektivitetsbedömning</h5>
                            <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                              {aiReview.kbt_analysis.effectiveness_assessment}
                            </p>
                          </div>
                        )}
                        
                        {/* Show recommendations if available */}
                        {aiReview.kbt_analysis.recommendations && (
                          <div className="border-l-3 border-purple-200 pl-2">
                            <h5 className="text-xs font-medium text-gray-900">Rekommendationer</h5>
                            <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                              {Array.isArray(aiReview.kbt_analysis.recommendations) 
                                ? aiReview.kbt_analysis.recommendations.join(', ')
                                : aiReview.kbt_analysis.recommendations}
                            </p>
                          </div>
                        )}
                        
                        {/* Show other KBT analysis fields */}
                        {Object.entries(aiReview.kbt_analysis)
                          .filter(([key]) => !['techniques_used', 'effectiveness_assessment', 'recommendations'].includes(key))
                          .map(([key, value]) => (
                            <div key={key} className="border-l-3 border-gray-200 pl-2">
                              <h5 className="text-xs font-medium text-gray-900 capitalize">
                                {key.replace(/_/g, ' ').replace(/assessment/g, 'bedömning')}
                              </h5>
                              <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                                {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                              </p>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}

                  {/* Improvement Suggestions - v2.0 compatible */}
                  {(aiReview.improvement_suggestions || aiReview.conversation_context?.improvement_suggestions) && (
                    <div className="bg-white border border-gray-200 rounded-lg p-3">
                      <h4 className="text-sm font-medium text-gray-900 mb-3">Förbättringsförslag</h4>
                      <ul className="space-y-1">
                        {(aiReview.improvement_suggestions || aiReview.conversation_context?.improvement_suggestions || []).map((suggestion, index) => (
                          <li key={index} className="flex items-start text-xs">
                            <div className="w-1 h-1 bg-blue-500 rounded-full mt-1.5 mr-2 flex-shrink-0"></div>
                            <span className="text-gray-700 leading-relaxed">{suggestion}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Alternative Approaches - v2.0 compatible */}
                  {(aiReview.alternative_approaches || aiReview.conversation_context?.alternative_approaches) && (
                    <div className="bg-white border border-gray-200 rounded-lg p-3">
                      <h4 className="text-sm font-medium text-gray-900 mb-3">Alternativa tillvägagångssätt</h4>
                      <ul className="space-y-1">
                        {(aiReview.alternative_approaches || aiReview.conversation_context?.alternative_approaches || []).map((approach, index) => (
                          <li key={index} className="flex items-start text-xs">
                            <div className="w-1 h-1 bg-green-500 rounded-full mt-1.5 mr-2 flex-shrink-0"></div>
                            <span className="text-gray-700 leading-relaxed">{approach}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Metadata */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                    <h4 className="text-sm font-medium text-gray-900 mb-3">Metadata</h4>
                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between">
                        <span className="text-gray-600">AI-modell:</span>
                        <span className="text-gray-900">{aiReview.claude_model_used || 'Claude 3.5'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Konfidans:</span>
                        <span className="text-gray-900">{((aiReview.ai_confidence || 0.85) * 100).toFixed(0)}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Säkerhetsrisk:</span>
                        <div className={`px-1 py-0.5 rounded text-xs font-medium ${
                          (aiReview.safety_risk || 0) >= 0.7 ? 'bg-red-100 text-red-800' :
                          (aiReview.safety_risk || 0) >= 0.4 ? 'bg-yellow-100 text-yellow-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {((aiReview.safety_risk || 0) * 10).toFixed(1)}/10
                        </div>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Schema version:</span>
                        <span className="text-gray-900">{aiReview.schema_version || '2.0'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Analyserad:</span>
                        <span className="text-gray-900">{formatTimestamp(aiReview.analysis_timestamp || aiReview.analyzed_at || aiReview.created_at)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-4 text-center">
                  <Brain className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <h4 className="text-sm font-medium text-gray-900 mb-1">Ingen AI-granskning</h4>
                  <p className="text-xs text-gray-600 mb-3">
                    Denna konversation har inte analyserats ännu.
                  </p>
                  
                  {/* Create AI Review Button */}
                  {highlightedMessageId && (
                    <button
                      onClick={createAIReview}
                      disabled={isCreatingReview}
                      className="mb-3 px-3 py-2 bg-purple-600 text-white text-xs rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 mx-auto"
                    >
                      {isCreatingReview ? (
                        <>
                          <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Skapar...
                        </>
                      ) : (
                        <>
                          <Brain className="w-3 h-3" />
                          Skapa AI Review
                        </>
                      )}
                    </button>
                  )}
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <div className="flex items-start">
                      <AlertTriangle className="w-4 h-4 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
                      <div className="text-xs text-blue-800">
                        <p className="font-medium mb-1">Varför finns ingen granskning?</p>
                        <p>AI Review analyserar endast nya konversationer automatiskt.</p>
                        {highlightedMessageId && (
                          <p className="mt-1 font-medium">💡 Klicka "Skapa AI Review" för att analysera det markerade meddelandet.</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* Collapsed state indicator */}
        {isCollapsed && (
          <div className="flex flex-col items-center justify-center h-full">
            <Brain className="w-6 h-6 text-gray-400 mb-2" />
            <span className="text-xs text-gray-500 transform -rotate-90 whitespace-nowrap">AI Review</span>
          </div>
        )}
      </div>
    </>
  );
};

export default AIReviewPanel;
